import { EyeInvisibleOutlined } from '@ant-design/icons';
import { Alert, Checkbox, Heading, HelpText, Line, Link } from '@mm/components';
import {
  EMAIL_LINKED_TO_ANOTHER_ACCOUNT,
  UNPROCESSABLE_ENTITY,
} from '@mm/constants/status-codes';
import { UTMTags } from '@mm/hooks';
import { EyeLight } from '@mm/icons';
import { useChatbotCRUDActions } from '@mm/state/actions/ChatbotCRUDActions';
import { useFacebookSessionCRUDActions } from '@mm/state/actions/FacebookSessionCRUDActions';
import sessionCRUDActions from '@mm/state/actions/SessionCRUDActions';
import usersCRUDActions from '@mm/state/actions/UsersCRUDActions';
import {
  LoginType,
  mapLoginTypeToPersona,
  Persona,
} from '@mm/state/controllers/SessionController';
import commonHandler from '@mm/state/errorHandlers/common-handler';
import JSONUtils from '@mm/state/helpers/json-utils';
import {
  OnboardingSignupStep,
  UserAttributes,
} from '@mm/state/model/CompanyUsersModel';
import { StoreState } from '@mm/state/store-interfaces';
import { getErrorJson, LocalStorageService } from '@mm/utils/index';
import classNames from 'classnames';
import * as queryString from 'query-string';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Dispatch } from 'redux';
import useRouter from 'use-react-router';
import FacebookLoginButton from '../../../../common/buttons/FacebookLoginButton/FacebookLoginButton';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../common/buttons/PrimaryButton/PrimaryButton';
import { PhoneInput } from '../../../../common/components/PhoneInput/PhoneInput';
import TextInput from '../../../../common/components/TextInput';
import { URL_REGEX } from '../../../../common/constants/validations';
import { PREV_LOCATION } from '../../../../common/helpers/routes/GuardedPrivateRoute';
import {
  EmailInput,
  useEmailInput,
} from '../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { colors } from '../../../../editor/features/beta/styles/colors';
import { Routes } from '../../../../routes/top-level-routes';
import { routes } from '../../routes';
import { PasswordValidation } from '../PasswordValidation/PasswordValidation';
import { checkStringComplexity } from './checkStringComplexity';
import './SignUpForm.pcss';
import { Spin } from 'antd';
import { useRegisterSignupEvent } from '../../../../dashboard/hooks/use-register-signup-event';
import { OnboardingRoutes } from '../../../../routes/onboarding-routes';
import onboardingActions from '@mm/state/modules/onboardingState/actions';
import { trafficTotalSelector } from '@mm/state/selectors/websiteReportSummarySelector';
import { extractDomainName } from '../../../../dashboard/pages/XRayOnboarding/WebsiteReportSummary/constants';
import { useGetWebsiteTraffic } from '../../../../dashboard/pages/XRayOnboarding/hooks/use-get-website-traffic';

export type SignUpFormProps = {
  type?: LoginType;
  inviteToken?: string;
  affiliateId?: string;
  requiredOnlyFields?: boolean;
  showEmail?: boolean;
  showFacebook?: boolean;
  heading?: string;
  aboveHeading?: string | ReactNode;
  subheading?: string;
  invitingCompanyId?: number;
  invitingCompanyName?: string;
  css?: {
    form?: string;
    container?: string;
    heading?: string;
    aboveHeading?: string;
    subheading?: string;
    submitBtn?: string;
    helpText?: string;
  };
  hideTrial?: boolean;
  forXrayOnboarding?: boolean;
  withPhone?: boolean;
  withPasswordConfirmation?: boolean;
  signUpButtonName?: string;
  websiteURL?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  withCompanyName?: boolean;
  withWebsite?: boolean;
  onSubmitSuccess?: () => void;
};

export const useSignup = (invitingCompanyId?: number, website?: string) => {
  const dispatch: Dispatch<StoreState> = useDispatch();
  const history = useHistory();
  const { retrieveBasicListByCompany } = useChatbotCRUDActions();
  const stringifiedPrevLocation = LocalStorageService.getItem(PREV_LOCATION);
  const prevLocation = JSONUtils.safeParse<Location>(stringifiedPrevLocation);
  const parsedPreviousPathname = prevLocation?.pathname?.split('/');
  const isCustomPlan =
    parsedPreviousPathname?.[1] === 'subscribe' &&
    !!parsedPreviousPathname?.[2];

  const totalTraffic = useSelector(trafficTotalSelector);
  const isSQL = totalTraffic.trafficTypes?.sql;
  const { registerSignUpEventWithTracking } = useRegisterSignupEvent();

  const { retrieveTotals } = useGetWebsiteTraffic({});
  const domainName = extractDomainName(website || '');

  const signUpEmail = async (
    ...args: Parameters<typeof sessionCRUDActions.signUp>
  ) => {
    const [obj] = args;
    try {
      const { inviteToken } = obj;

      const { totals, data } =
        (domainName &&
          !Object.keys(totalTraffic).length &&
          (await retrieveTotals(domainName))) ||
        {};

      const shouldBookDemo =
        (totals ? totals?.trafficTypes?.sql : isSQL) ||
        data?.country_code === 'US';

      const session = await dispatch(
        sessionCRUDActions.signUp({
          ...obj,
          userAttributes: {
            ...obj.userAttributes,
            onboardingSignupStep:
              inviteToken || !shouldBookDemo
                ? OnboardingSignupStep.NONE
                : OnboardingSignupStep.BOOK_DEMO,
            freeTrial: !isCustomPlan,
          },
        }),
      );

      if (inviteToken) {
        // clear token to avoid fetch bot data inner magic,
        // signup route takes care of invite
        await dispatch(sessionCRUDActions.resetInviteToken());
      }

      const companyId =
        invitingCompanyId || session?.userAttributes?.activeCompanyId;
      let activeBot;

      if (companyId) {
        activeBot = await retrieveBasicListByCompany(companyId);
        await dispatch(
          usersCRUDActions.updateActiveCompanyId(session.id, companyId),
        );
      }

      if (inviteToken) {
        dispatch(onboardingActions.setJustCompletedOnboarding(true));
      }

      // set to affect modal open ordering
      if (!session.userAttributes.mmBuyBeforeTrying) {
        const chatbotId = activeBot?.[0]?.id;
        if (shouldBookDemo) {
          history.push(OnboardingRoutes.bookDemo.path);
        } else {
          registerSignUpEventWithTracking({
            nextOnboardingStep: OnboardingSignupStep.NONE,
            options: {
              session,
              companyId,
              chatbotId,
            },
          });
        }
      }
    } catch (e) {
      if (e instanceof Response) {
        const responseErrorBody = await getErrorJson(e);
        if (
          e.status === UNPROCESSABLE_ENTITY &&
          responseErrorBody.error_code === EMAIL_LINKED_TO_ANOTHER_ACCOUNT &&
          responseErrorBody.errors
        ) {
          return responseErrorBody.errors[0];
        }
      }
      return commonHandler(dispatch)(e);
    }
  };

  const { create: createFBSession } = useFacebookSessionCRUDActions();
  const signUpFacebook = async ({
    facebookToken,
    inviteToken,
    userAttributes,
  }: {
    facebookToken: string;
    inviteToken?: string;
    userAttributes?: { signupForm?: Persona } & Pick<
      UserAttributes,
      'smsConsent'
    >;
  }) => {
    const opts = {
      facebookToken,
      inviteToken,
      userAttributes: {
        ...userAttributes,
        freeTrial: !isCustomPlan,
      },
    };

    const response = await createFBSession(opts);
    if (response && !response.error) {
      const path = response.userAttributes.mmBuyBeforeTrying
        ? Routes.subscribe.getLink()
        : Routes.chatbotEditor.path;
      history.push(path);
    }
  };

  return {
    signUpEmail,
    signUpFacebook,
  };
};

export const PASSWORD_LENGTH = 8;

export const usePasswordValidation = password =>
  useMemo(() => {
    const complexity = checkStringComplexity(password);
    return !!(
      complexity.total >= PASSWORD_LENGTH &&
      complexity.lowercase &&
      complexity.uppercase &&
      complexity.numbers &&
      complexity.specials
    );
  }, [password]);

// tslint:disable-next-line: cyclomatic-complexity
export const SignUpForm = ({
  css,
  inviteToken,
  requiredOnlyFields,
  heading = 'Sign up',
  aboveHeading,
  subheading = '',
  showEmail = true,
  showFacebook = true,
  type,
  invitingCompanyId,
  invitingCompanyName,
  hideTrial,
  forXrayOnboarding,
  withPhone = false,
  withPasswordConfirmation = true,
  signUpButtonName,
  websiteURL,
  email: initialEmail,
  firstName: initialFirstName,
  lastName: initialLastName,
  companyName: initialCompanyName,
  onSubmitSuccess,
  withCompanyName = false,
  withWebsite = false,
}: SignUpFormProps) => {
  const utmObj = UTMTags.get();
  const showRequiredOnlyFields = requiredOnlyFields || inviteToken;
  const signupForm = mapLoginTypeToPersona(type);
  const isSignUpForMobile = type === LoginType.mobile;

  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordControl, setPasswordControl] = useState('');
  const [firstName, setFirstName] = useState(initialFirstName || '');
  const [lastName, setLastName] = useState(initialLastName || '');
  const [companyName, setCompanyName] = useState(
    invitingCompanyName || initialCompanyName || '',
  );
  const [website, setWebsite] = useState(websiteURL || '');
  const [phone, setPhone] = useState('');

  const [isSmsMarketingChecked, setIsSmsMarketingChecked] = useState(false);

  const campaignEmail = useSelector(
    (state: StoreState) => state.signUpForm.email,
  );
  const { emailState, onChange } = useEmailInput(
    initialEmail || campaignEmail || '',
  );

  const passwordMatch = password === passwordControl;
  const isPasswordValid = usePasswordValidation(password);

  const {
    location: { search },
  } = useRouter();

  let fieldsForValidation: (string | boolean)[] = [
    password,
    firstName,
    lastName,
  ];

  if (!showRequiredOnlyFields) {
    fieldsForValidation = [
      ...fieldsForValidation,
      companyName,
      website && URL_REGEX.test(website),
    ];
  }

  if (withCompanyName) {
    fieldsForValidation = [...fieldsForValidation, companyName];
  }

  if (withWebsite) {
    fieldsForValidation = [
      ...fieldsForValidation,
      website && URL_REGEX.test(website),
    ];
  }

  const isValid =
    emailState.isValid &&
    isPasswordValid &&
    fieldsForValidation.every(field =>
      typeof field === 'string' ? field.length : Boolean(field),
    );

  const { signUpEmail, signUpFacebook } = useSignup(invitingCompanyId, website);

  useEffect(() => {
    if (type === LoginType.whiteLabel && search) {
      const { facebookToken } = queryString.parse(search);
      if (typeof facebookToken === 'string') {
        signUpFacebook({
          facebookToken,
          userAttributes: { smsConsent: isSmsMarketingChecked },
        });
      }
    }
  }, []);

  // to prevent UI flickering on rerender caused by token reset
  const [isSigningUpWithToken, setIsSigningUpWithToken] = useState(false);
  const [showPasswordValidation, setShowPasswordValidation] = useState(false);
  const [accountError, setAccountError] = useState('');
  const complexity = checkStringComplexity(password);

  const onSubmit = async e => {
    e.preventDefault();
    setIsSigningUpWithToken(!!inviteToken);

    const onboardingReportVersion = localStorage.getItem(
      'onboardingReportVersion',
    );
    setBusy(true);

    const res = await signUpEmail({
      email: emailState.email,
      password,
      firstName,
      lastName,
      phone,
      companyName,
      website,
      inviteToken,
      additionalFeatures: forXrayOnboarding ? ['xray_onboarding'] : undefined,
      userAttributes: {
        ...(forXrayOnboarding && { onboardingReportVersion }),
        signupForm,
        smsConsent: isSmsMarketingChecked,
        ...utmObj,
      },
    });
    setBusy(false);
    if (!res) {
      onSubmitSuccess?.();
    }

    if (res) {
      setAccountError(res);
    }
  };

  const [passwordVisible, setPasswordVisible] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  const signInLinkTo =
    type === LoginType.whiteLabel || inviteToken
      ? routes.signin.compile(type) + search
      : routes.signin.compile(type);

  const labelClassNames = (diabledCondition: boolean, isInput: boolean) =>
    classNames(
      isInput ? 'sign-up-form__input' : 'sign-up-form__input-label',
      isInput
        ? {
            'sign-up-form__input-invalid': diabledCondition && showErrors,
          }
        : {
            'sign-up-form__input-label-invalid': diabledCondition && showErrors,
          },
    );

  useEffect(() => {
    if (isPasswordValid) {
      setShowPasswordValidation(false);
    }
  }, [isPasswordValid]);

  return (
    <Spin spinning={busy} size="small" tip="Processing...">
      <form onSubmit={onSubmit} className={css?.form}>
        <div className={classNames(css?.container, 'sign-up-form')}>
          {aboveHeading && (
            <div className={css?.aboveHeading}>{aboveHeading}</div>
          )}
          <Heading
            className={classNames(css?.heading, 'sign-up-form__heading')}
            size={4}
          >
            {heading}
          </Heading>

          {subheading && (
            <Heading
              className={classNames(
                css?.subheading,
                'sign-up-form__subheading',
              )}
              size={4}
            >
              {subheading}
            </Heading>
          )}
          {!!showFacebook && !isSignUpForMobile && (
            <FacebookLoginButton
              className="sign-up-form__facebook-btn"
              onSuccess={({ facebookToken }: { facebookToken: string }) =>
                signUpFacebook({
                  facebookToken,
                  inviteToken,
                  userAttributes: {
                    signupForm,
                    ...utmObj,
                  },
                })
              }
              label="Sign up with Facebook"
            />
          )}

          {(!forXrayOnboarding ||
            (!forXrayOnboarding && !isSignUpForMobile)) && (
            <Line className="sign-up-form__separator" text="or with email" />
          )}

          {!!showEmail && (
            <>
              {accountError && (
                <Alert
                  className="sign-up-form__alert"
                  showCloseIcon={false}
                  type="error"
                >
                  {accountError}
                </Alert>
              )}
              <div className="sign-up-form__input-group-row">
                <div className="sign-up-form__input-group">
                  <label className={labelClassNames(!firstName, false)}>
                    First Name
                  </label>

                  <TextInput
                    className={labelClassNames(!firstName, true)}
                    onChange={e => setFirstName(e.target.value)}
                    aria-label="First name"
                    type="text"
                    value={firstName}
                  />
                </div>

                <div className="sign-up-form__input-group">
                  <label className={labelClassNames(!lastName, false)}>
                    Last Name
                  </label>
                  <TextInput
                    className={labelClassNames(!lastName, true)}
                    onChange={e => setLastName(e.target.value)}
                    aria-label="Last name"
                    type="text"
                    value={lastName}
                  />
                </div>
              </div>
              <div className="sign-up-form__input-group">
                <label className={labelClassNames(!emailState.isValid, false)}>
                  {' '}
                  Business Email
                </label>
                <EmailInput
                  onChange={onChange}
                  inputProps={{
                    className: labelClassNames(!emailState.isValid, true),
                    placeholder: ' ',
                  }}
                  withIcon={false}
                  strict
                  defaultValue={emailState.email}
                />
                {forXrayOnboarding && (
                  <div className="sign-up-form__input-note">
                    Where should we send the leads to?
                  </div>
                )}
              </div>

              {!isSigningUpWithToken && withCompanyName && (
                <div className="sign-up-form__input-group">
                  <label className={labelClassNames(!companyName, false)}>
                    Company Name
                  </label>
                  <TextInput
                    className={labelClassNames(!companyName, true)}
                    onChange={e => setCompanyName(e.target.value)}
                    aria-label="Company"
                    value={companyName}
                    type="text"
                  />
                </div>
              )}

              {!isSigningUpWithToken && withWebsite && (
                <div className="sign-up-form__input-group">
                  <label className={labelClassNames(!website, false)}>
                    Company Website
                  </label>
                  <TextInput
                    className={labelClassNames(!website, true)}
                    onChange={e => setWebsite(e.target.value)}
                    aria-label="Business Website"
                    type="text"
                    placeholder="yourdomain.com"
                    value={website}
                  />
                </div>
              )}

              {withPhone && (
                <div className="sign-up-form__input-group">
                  <label className={labelClassNames(!phone, false)}>
                    {' '}
                    Phone Number
                  </label>
                  <PhoneInput
                    className={classNames('sign-up-form__phone-input', {
                      'sign-up-form__phone-input-invalid': !phone && showErrors,
                    })}
                    value={phone}
                    onChange={val => setPhone(val)}
                  />
                  {forXrayOnboarding && (
                    <div className="sign-up-form__input-note">
                      We will notify when your leads are ready
                    </div>
                  )}
                </div>
              )}

              <div
                onFocus={() => setShowPasswordValidation(true)}
                onBlur={() => setShowPasswordValidation(false)}
                className="sign-up-form__input-group"
              >
                <label className={labelClassNames(!isPasswordValid, false)}>
                  Password
                </label>
                <TextInput
                  className={labelClassNames(!isPasswordValid, true)}
                  onChange={e => setPassword(e.target.value)}
                  error={!!(password.length && !isPasswordValid)}
                  aria-label="password"
                  placeholder="Password"
                  type={passwordVisible[1] ? 'text' : 'password'}
                  autoComplete="off"
                />
                <span
                  className={classNames(
                    'sign-up-form__password-icon',
                    'first',
                    {
                      'sign-up-form__password-icon-error':
                        !isPasswordValid && showErrors,
                    },
                  )}
                  onClick={() =>
                    setPasswordVisible({
                      ...passwordVisible,
                      1: !passwordVisible[1],
                    })
                  }
                >
                  {passwordVisible[1] ? (
                    <EyeLight color={colors.accentBlue} />
                  ) : (
                    <EyeInvisibleOutlined className="sign-up-form__eye-invisible " />
                  )}
                </span>
                {forXrayOnboarding && (
                  <div className="sign-up-form__input-note">
                    To secure your lead access
                  </div>
                )}
              </div>
              {showPasswordValidation && (
                <div
                  className={classNames('sign-up-form__password-validation', {
                    'sign-up-form__password-validation-valid': isPasswordValid,
                  })}
                >
                  <PasswordValidation
                    text="Contains at least 8 characters"
                    error={password.length < PASSWORD_LENGTH}
                    childClassName={
                      forXrayOnboarding
                        ? 'sign-up-form__password-validation-error'
                        : ''
                    }
                    withIcon={false}
                    hideWithoutError
                  />
                  <PasswordValidation
                    text="Use upper & lower case letters"
                    error={complexity.lowercase < 1 || complexity.uppercase < 1}
                    childClassName={
                      forXrayOnboarding
                        ? 'sign-up-form__password-validation-error'
                        : ''
                    }
                    withIcon={false}
                    hideWithoutError
                  />
                  <PasswordValidation
                    text="Use special character & digit"
                    error={complexity.numbers < 1 || complexity.specials < 1}
                    childClassName={
                      forXrayOnboarding
                        ? 'sign-up-form__password-validation-error'
                        : ''
                    }
                    withIcon={false}
                    hideWithoutError
                  />
                </div>
              )}
              {withPasswordConfirmation && (
                <div className="sign-up-form__input-group">
                  <TextInput
                    className="sign-up-form__input"
                    onChange={e => setPasswordControl(e.target.value)}
                    error={!!(passwordControl.length && !passwordMatch)}
                    aria-label="password confirmation"
                    placeholder="Password confirmation"
                    type={passwordVisible[2] ? 'text' : 'password'}
                    autoComplete="off"
                  />
                  <span
                    className="sign-up-form__password-icon"
                    onClick={() =>
                      setPasswordVisible({
                        ...passwordVisible,
                        2: !passwordVisible[2],
                      })
                    }
                  >
                    {passwordVisible[2] ? (
                      <EyeLight color={colors.accentBlue} />
                    ) : (
                      <EyeInvisibleOutlined className="sign-up-form__eye-invisible" />
                    )}
                  </span>
                </div>
              )}

              <div
                onClick={() => setIsSmsMarketingChecked(!isSmsMarketingChecked)}
                className="sign-up-form__sms-marketing"
              >
                <Checkbox
                  name="sms_marketing"
                  isChecked={isSmsMarketingChecked}
                  className="sign-up-form__sms-marketing-checkbox"
                />
                <span className="sign-up-form__sms-marketing-label">
                  <span>Agree to receive SMS marketing messages.</span>
                  <span>
                    Please note that you will receive SMS marketing messages
                    through our website, not by text message. Standard message
                    rates may apply. You can unsubscribe anytime by replying
                    STOP.
                  </span>
                </span>
              </div>

              <div className="sign-up-form__cta-btn-wrapper">
                {!isValid && (
                  <div
                    className="sign-up-form__cta-btn-clickable"
                    onClick={() => setShowErrors(true)}
                  />
                )}
                <PrimaryButton
                  disabled={!isValid}
                  className={classNames(
                    css?.submitBtn,
                    'sign-up-form__cta-btn',
                  )}
                  color={ButtonColor.blue}
                  type="submit"
                >
                  {signUpButtonName
                    ? signUpButtonName
                    : invitingCompanyId || inviteToken || hideTrial
                    ? 'Create Account'
                    : 'Sign Up'}
                </PrimaryButton>
              </div>

              <HelpText
                className={classNames(
                  'sign-up-form__footer-actions',
                  css?.helpText,
                )}
              >
                <>
                  By signing up, you agree to Customers.ai’s &nbsp;
                  <Link
                    className="sign-up-form__link"
                    href={Routes.masterServiceAgreement.getLink()}
                    rel="noopener noreferrer"
                    isBlank
                  >
                    Terms of Service
                  </Link>
                  &nbsp;and&nbsp;
                  <Link
                    className="sign-up-form__link"
                    href={Routes.privacyPolicy.getLink()}
                    rel="noopener noreferrer"
                    isBlank
                  >
                    Privacy Policy
                  </Link>
                </>
              </HelpText>

              {forXrayOnboarding
                ? !forXrayOnboarding
                : !isSignUpForMobile && (
                    <HelpText
                      className="sign-up-form__footer-actions"
                      text={
                        <span>
                          Already have an account?{' '}
                          <Link
                            className="sign-up-form__link-button"
                            to={signInLinkTo}
                          >
                            Log in
                          </Link>
                        </span>
                      }
                    />
                  )}
            </>
          )}
        </div>
      </form>
    </Spin>
  );
};
